import React, { useRef, useState } from 'react';

import { CountdownTimer, InputSmsCode, Loader } from 'components/common/index';
import smsConfig from 'configs/sms';
import {
  useEffectOnMount,
  useEffectOnUpdate,
  useErrorCallback,
  useLoading,
  useProfileData,
} from 'hooks';
import { CSSTransition } from 'react-transition-group';

import { Phone } from '../../../../api-client';
import { createSmsFormStateStorage } from '../../../../di';
import formatPhone from '../../../../formatters/formatPhone';
import { cn } from '../../../../utils';
import { Button, PrimaryButton } from '../../buttons';
import { PrimaryButtonColor } from '../../buttons/decorators/PrimaryButton/PrimaryButton';

import './ConfirmSmsCodeForm.scoped.scss';

type Props<TResult = void> = {
  sendCallback: (code: string) => Promise<TResult>;
  resendCallback: () => Promise<void>;
  successCallback: (result: TResult) => Promise<void> | void;
  prefix: string;
  phone?: Phone;
  variant?: 'default' | 'comfort';
  title?: string;
  onCancel?: () => void;
  onError?: (error: Error) => void | Promise<void>;
  compactLoading?: boolean;
};

const maxSecondsToAttempt = smsConfig.smsCodeTimeout;
const smsCodeLength = smsConfig.smsCodeLength > 0 ? smsConfig.smsCodeLength : 6;
const maxAttempts = smsConfig.smsMaxAttempts;

const ConfirmSmsCodeForm = <TResult,>({
  sendCallback,
  resendCallback,
  successCallback,
  phone,
  prefix,
  variant = 'default',
  title = 'Введите код',
  onCancel,
  onError,
  compactLoading = false,
}: Props<TResult>) => {
  const defaultErrorCallback = useErrorCallback();
  const [code, setCode] = useState('');
  const [isFilled, setIsFilled] = useState(false);
  const [numberAttempts, setNumberAttempts] = useState(maxAttempts);
  const [isLoading, withLoading] = useLoading();
  const [newCodeRequested, setNewCodeRequested] = useState(false);
  const [timeLeft, setTimeLeft] = useState(maxSecondsToAttempt);
  const profileData = useProfileData();
  const [active, setActive] = useState(false);
  const timeoutRef = useRef(0);
  const isComfort = variant === 'comfort';

  const storage = createSmsFormStateStorage(prefix);

  const phoneTextToShow = () => {
    if (phone !== undefined) {
      return `номер ${formatPhone(phone)}`;
    }
    if (profileData.phone !== undefined) {
      return `номер ${formatPhone(profileData.phone)}`;
    }
    return 'Ваш номер';
  };

  const resendCode = async () => {
    const resendCodeCallback = async () => {
      try {
        await resendCallback();
        setNewCodeRequested(true);
        setNumberAttempts(maxAttempts);
      } catch (err: unknown) {
        if (err instanceof Error) {
          await defaultErrorCallback(err);
        }
      }
    };
    await withLoading(resendCodeCallback);
  };

  useEffectOnUpdate(() => {
    const callback = () => {
      if (newCodeRequested) {
        const date = new Date();
        date.setSeconds(date.getSeconds() + timeLeft);
        storage.set({
          attemptsLeft: numberAttempts,
          nextTryDate: date,
        });
      } else {
        storage.remove();
        setTimeLeft(maxSecondsToAttempt);
      }
    };
    callback();
    return () => {
      callback();
    };
  }, [newCodeRequested]);

  useEffectOnUpdate(() => {
    storage.setAttempts(numberAttempts);
  }, [numberAttempts]);

  useEffectOnMount(() => {
    const mountCallback = () => {
      const data = storage.get();
      if (data) {
        setNumberAttempts(data.attemptsLeft);
        if (data.nextTryDate) {
          const timeDiff = Math.round((data.nextTryDate.getTime() - new Date().getTime()) / 1000);
          if (timeDiff > 0) {
            setTimeLeft(timeDiff);
            setNewCodeRequested(true);
          } else {
            storage.remove();
            setTimeLeft(maxSecondsToAttempt);
          }
        }
      }
    };
    mountCallback();
  });

  useEffectOnUpdate(() => {
    setIsFilled(smsCodeLength === code.length);
  }, [code]);

  const sendCode = async () => {
    const sendCodeCallback = async () => {
      try {
        const result = await sendCallback(code);
        storage.remove();
        await successCallback(result);
      } catch (err: unknown) {
        if (err instanceof Error) {
          if (onError) {
            await onError(err);
          } else {
            await defaultErrorCallback(err);
          }
        }
        setNumberAttempts((prevNumber) => prevNumber - 1);
      }
    };
    await withLoading(sendCodeCallback);
  };

  const onHover = () => {
    clearTimeout(timeoutRef.current);
    setActive(true);
  };

  const onLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setActive(false);
    }, 250);
  };

  const onTimerExpire = () => {
    setNewCodeRequested(false);
  };

  if (isComfort && isLoading) {
    return (
      <div className={cn('confirm-form', 'confirm-form--comfort')}>
        <div
          className={cn(
            'confirm-form__comfort-loading',
            compactLoading ? 'confirm-form__comfort-loading--compact' : undefined
          )}
        >
          <Loader size={compactLoading ? 'small' : 'large'} overlay={false} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('confirm-form', isComfort ? 'confirm-form--comfort' : undefined)}>
      {isComfort ? (
        <>
          <h3 className="confirm-form__comfort-title">{title}</h3>
          <p className="confirm-form__comfort-hint">
            Мы отправили SMS с кодом на {phoneTextToShow()}
          </p>
        </>
      ) : (
        <div className="confirm-form__header">
          Мы отправили код подтверждения на {phoneTextToShow()}
        </div>
      )}

      <div
        className={cn(
          'confirm-form__input',
          isComfort ? 'confirm-form__input--comfort' : undefined
        )}
      >
        <InputSmsCode onChange={setCode} code={code} />
        {isFilled ? (
          <PrimaryButton expanded color={PrimaryButtonColor.GREEN}>
            <Button onClick={sendCode} disabled={isLoading}>
              {isComfort ? 'Подтвердить' : 'Отправить'}
            </Button>
          </PrimaryButton>
        ) : null}
      </div>

      {isComfort && onCancel ? (
        <button type="button" className="confirm-form__comfort-cancel" onClick={onCancel}>
          Отмена
        </button>
      ) : null}

      <div
        className={cn(
          'confirm-form__controls',
          isComfort ? 'confirm-form__controls--comfort' : undefined
        )}
      >
        {isLoading && !isComfort ? <Loader size="small" overlay={false} /> : null}
        {newCodeRequested ? (
          <div className="confirm-form__countdown">
            {isComfort ? 'Отправить код повторно можно через ' : 'Получить новый код можно через '}
            <CountdownTimer seconds={timeLeft} callback={onTimerExpire} />
          </div>
        ) : (
          <button
            type="button"
            onClick={resendCode}
            className={isComfort ? 'confirm-form__comfort-resend' : 'confirm-form__action'}
          >
            {isComfort ? 'Отправить код повторно' : 'Получить новый код'}
          </button>
        )}
        {!isComfort ? (
          <div className="sms-help" onMouseEnter={onHover} onMouseLeave={onLeave}>
            <Button className="confirm-form__action">Не приходит СМС?</Button>
            <CSSTransition in={active} timeout={250} classNames="sms-help__answer-">
              <div className="sms-help__answer">
                <div className="sms-help__text">
                  1. Проверьте, верно ли указан номер телефона и отправьте запрос на получение{' '}
                  <b>кода</b> ещё раз. <br />
                  <br />
                  2. Перезагрузите телефон и проверьте наличие <b>СМС.</b> <br />
                  <br />
                  3. Освободите память в телефоне. Возможно, её недостаточно, чтобы получить
                  сообщение. <br />
                  <br />
                  4. Повторите попытку немного позже.
                </div>
              </div>
            </CSSTransition>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ConfirmSmsCodeForm;
